// src/modules/assetManagement/useAssetQuery.ts
// Runs an async loader and re-runs it after any asset mutation. Keeps the last data while reloading.
import { useCallback, useEffect, useRef, useState } from "react";
import { subscribeAssetChanges } from "./assetApi";

export function useAssetQuery<T>(loader: () => Promise<T>, deps: unknown[]) {
  const [data, setData] = useState<T | undefined>(undefined);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const requestId = useRef(0);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const load = useCallback(loader, deps);

  const run = useCallback(() => {
    const id = ++requestId.current;
    setLoading(true);
    load()
      .then((result) => {
        if (id !== requestId.current) return;
        setData(result);
        setError(null);
      })
      .catch((e: unknown) => {
        if (id === requestId.current) setError(e instanceof Error ? e.message : "Something went wrong");
      })
      .finally(() => {
        if (id === requestId.current) setLoading(false);
      });
  }, [load]);

  useEffect(() => {
    run();
    return subscribeAssetChanges(run);
  }, [run]);

  return { data, loading, error, initialLoading: loading && data === undefined && !error, reload: run };
}
